/**
 * formatAnswer
 * ------------
 * Transforms the raw value from `formData` into a human-readable string
 * for the Review step and any other read-only summaries.
 *
 * Behaviour by field type:
 *   • "date"   → ISO string ⇒ locale-formatted date (falls back to raw).
 *   • "select" → option value ⇒ corresponding option.label (falls back to raw).
 *   • default  → returns the raw value unchanged.
 *
 * Edge cases:
 *   • `null`, `undefined`, or `""`  → returns "N/A".
 *
 * @param {object} question – Question definition (must contain `id`, `fieldType`,
 *                            and for "select" fields: `options[{label,value}]`).
 * @param {object} formData – Map of answers keyed by question.id.
 * @returns {string}         Human-friendly string representation.
 *
 * @example
 *   formatAnswer({ id: 'dob', fieldType: 'date' }, { dob: '1990-01-31' })
 *   // → "31/01/1990"  (depending on locale)
 */

export default function formatAnswer(question, formData) {
  const raw = formData[question.id];

  // Handle empty or missing answers up-front
  if (raw == null || raw === "") return "N/A";

  switch (question.fieldType) {
    /* -------------------------------------------------------------- */
    /* Date → locale string                                           */
    /* -------------------------------------------------------------- */
    case "date": {
      const date = new Date(raw);
      return Number.isNaN(date) ? raw : date.toLocaleDateString();
    }

    /* -------------------------------------------------------------- */
    /* Select → look up label                                         */
    /* -------------------------------------------------------------- */
    case "select": {
      const option = question.options?.find((opt) => opt.value === raw);
      return option ? option.label : raw;
    }

    /* -------------------------------------------------------------- */
    /* Default: return raw string                                     */
    /* -------------------------------------------------------------- */
    default:
      return raw;
  }
}
